import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ArrowUpRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import Magnetic from "./motion/Magnetic";

const MotionLink = motion(Link);

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Mortgage Solutions", to: "/solutions" },
  { label: "Mortgage Calculator", to: "/calculator" },
  { label: "About", to: "/about" },
  { label: "Resources", to: "/resources" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

const PHONE_DISPLAY = "(825) 736-2193";
const PHONE_TEL = "tel:+18257362193";

const EASE = [0.22, 1, 0.36, 1];

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.45, ease: EASE, when: "beforeChildren", staggerChildren: 0.06, delayChildren: 0.12 },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.25, ease: EASE },
  },
};

const maskVariants = {
  hidden: { y: "112%" },
  visible: { y: "0%", transition: { duration: 0.75, ease: EASE } },
  // no exit variant — children leave with the overlay's own fade, so an early
  // tap mid-entrance can never stall AnimatePresence's exit/unmount
};

const fadeVariants = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

export default function Navbar({ onLead }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const lenis = window.__lenis;
    if (open) {
      document.body.style.overflow = "hidden";
      lenis?.stop();
    } else {
      document.body.style.overflow = "";
      lenis?.start();
    }
    return () => {
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [open]);

  // Guarantee: the menu can never stay open over a new page — any route
  // change forces it closed (covers link taps, back/forward, programmatic nav).
  useEffect(() => {
    setOpen(false);
    document.body.style.overflow = "";
    window.__lenis?.start();
  }, [pathname]);

  // Escape key closes the menu too
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        document.body.style.overflow = "";
        window.__lenis?.start();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const closeMenu = () => {
    setOpen(false);
    document.body.style.overflow = "";
    window.__lenis?.start();
  };

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow,backdrop-filter] duration-500 ${
        open
          ? "bg-transparent border-b border-transparent"
          : scrolled
            ? "bg-charcoal/70 backdrop-blur-2xl border-b border-gold/15 shadow-[0_12px_40px_rgba(0,0,0,0.45)]"
            : "bg-transparent border-b border-transparent"
      }`}
    >
      <div
        className={`relative z-50 max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between transition-[height] duration-500 ${
          scrolled && !open ? "h-16" : "h-20"
        }`}
      >
        <Link to="/" data-testid="nav-logo" className="leading-none group" onClick={closeMenu}>
          <span className="font-serif text-lg tracking-[0.2em] text-ivory transition-colors duration-300 group-hover:text-champagne">
            MY FIRST HOME
          </span>
          <span className="block text-[10px] tracking-[0.5em] text-gold font-medium mt-1">MORTGAGE LTD</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-2">
          {LINKS.map((l) => {
            const active = pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                aria-current={active ? "page" : undefined}
                className={`nav-link px-3 py-2 text-[13px] tracking-wide rounded-sm hover:bg-white/[0.04] ${
                  active ? "nav-link-active text-gold" : "text-ivory/70 hover:text-gold"
                }`}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:block">
          <Magnetic strength={0.25}>
            <button
              data-testid="nav-book-consultation-btn"
              onClick={() => onLead("consultation")}
              className="btn-sweep px-6 py-2.5 text-xs tracking-[0.18em] font-medium text-charcoal bg-gold rounded-sm"
            >
              <span className="btn-sweep-text">BOOK A CONSULTATION</span>
            </button>
          </Magnetic>
        </div>

        <button
          data-testid="nav-mobile-menu-btn"
          className="lg:hidden text-ivory p-2 relative w-9 h-9 flex items-center justify-center"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          <AnimatePresence mode="wait" initial={false}>
            {open ? (
              <motion.span
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.25, ease: EASE }}
              >
                <X size={22} strokeWidth={1.5} />
              </motion.span>
            ) : (
              <motion.span
                key="menu"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.25, ease: EASE }}
              >
                <Menu size={22} strokeWidth={1.5} />
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            data-testid="nav-mobile-menu"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{ pointerEvents: open ? "auto" : "none" }}
            className="lg:hidden fixed inset-0 z-40 bg-charcoal/95 backdrop-blur-2xl flex flex-col"
          >
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse 80% 50% at 50% -10%, rgba(229,192,123,0.08), transparent 70%)",
              }}
              aria-hidden="true"
            />

            <nav className="flex-1 flex flex-col justify-center px-8 pt-20">
              {LINKS.map((l, i) => (
                <div key={l.to} className="overflow-hidden border-b border-white/[0.06]">
                  <MotionLink
                    to={l.to}
                    variants={maskVariants}
                    onClick={closeMenu}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group flex items-baseline gap-4 py-4"
                  >
                    <span className="text-[11px] tracking-[0.3em] text-gold/60 font-medium">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className={`font-serif text-3xl transition-all duration-300 group-hover:text-gold group-hover:translate-x-1.5 ${
                      pathname === l.to ? "text-gold" : "text-ivory/85"
                    }`}>
                      {l.label}
                    </span>
                    <ArrowUpRight
                      size={18}
                      strokeWidth={1.5}
                      className="ml-auto text-gold/0 transition-all duration-300 group-hover:text-gold/70 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                    />
                  </MotionLink>
                </div>
              ))}
            </nav>

            <div className="px-8 pb-10 space-y-3">
              <motion.div variants={fadeVariants}>
                <button
                  data-testid="nav-mobile-preapproved-btn"
                  onClick={() => {
                    closeMenu();
                    onLead("pre-approval");
                  }}
                  className="btn-sweep w-full px-6 py-4 text-xs tracking-[0.22em] font-semibold text-charcoal bg-gold rounded-sm"
                >
                  <span className="btn-sweep-text">GET PRE-APPROVED</span>
                </button>
              </motion.div>
              <motion.div variants={fadeVariants}>
                <a
                  href={PHONE_TEL}
                  data-testid="nav-mobile-call-link"
                  className="w-full flex items-center justify-center gap-3 px-6 py-4 text-xs tracking-[0.22em] font-medium text-ivory/85 border border-white/15 hover:border-gold/50 hover:text-gold transition-colors duration-300 rounded-sm"
                >
                  <Phone size={14} strokeWidth={1.5} className="text-gold" />
                  CALL AKASHDEEP
                  <span className="text-ivory/50 tracking-normal">{PHONE_DISPLAY}</span>
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
